import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ButtonBase,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TodayIcon from '@mui/icons-material/Today';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import 'dayjs/locale/th';
import api from '../../utils/functions/api';

const apiBase = process.env.REACT_APP_API_URL;
const isImageUrl = (url) => /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url || '');
const hasSummary = (r) => !!(r.summary || r.attachments?.length);
const personKey = (r) => String(r.employee || r.employeeName);

// แท็บประเภทพนักงาน: FULLTIME = รายเดือน, ที่เหลือทั้งหมดนับเป็นรายวัน (ตรงกับที่ระบบอื่นแปลง)
const TYPE_TABS = ['daily', 'monthly'];
const isMonthly = (r) => r.employeeType === 'FULLTIME';

const FILTERS = [
  ['all', 'ทั้งหมด'],
  ['pending', 'ยังไม่สรุป'],
  ['done', 'สรุปแล้ว'],
];

// การ์ดสรุปงาน 1 รายการ — ข้อความยาวตัดที่ 5 บรรทัด กดที่การ์ดเพื่อเปิด modal อ่านเต็ม
// modal อ่านสรุปงานเต็ม: ชื่อ/วันที่/เวลา/โครงการ + ข้อความทั้งหมด + ไฟล์แนบขนาดใหญ่
function SummaryDetailDialog({ r, onClose }) {
  return (
    <Dialog open={!!r} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      {r && (
        <>
          <DialogTitle sx={{ pr: 6, pb: 1 }}>
            <Typography fontWeight={700}>{r.employeeName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {dayjs(r.checkIn).locale('th').format('D MMMM YYYY')} · {dayjs(r.checkIn).format('HH:mm')}
              {r.checkOut ? ` – ${dayjs(r.checkOut).format('HH:mm')}` : ' (ยังไม่ลงเวลาออก)'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {r.project?.project_number ? `${r.project.project_number} · ` : ''}{r.project?.name || '-'}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 10, right: 10 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {r.summary ? (
              <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.summary}</Typography>
            ) : (
              <Typography color="text.secondary">
                {r.attachments?.length ? 'ไม่มีข้อความ มีเฉพาะไฟล์แนบ' : 'ยังไม่ได้เขียนสรุป'}
              </Typography>
            )}
            {r.attachments?.length > 0 && (
              <Stack spacing={1} sx={{ mt: 2 }}>
                {r.attachments.map((f) => (isImageUrl(f.url) ? (
                  <a key={f.url} href={f.url} target="_blank" rel="noreferrer">
                    <img
                      src={f.url}
                      alt={f.name}
                      style={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 8, border: '1px solid #e5e7eb', display: 'block', background: '#f8fafc' }}
                    />
                  </a>
                ) : (
                  <Chip key={f.url} component="a" href={f.url} target="_blank" clickable label={f.name || 'ไฟล์แนบ'} sx={{ alignSelf: 'flex-start', maxWidth: '100%' }} />
                )))}
              </Stack>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

function SummaryCard({ r, onOpen }) {
  const done = hasSummary(r);
  return (
    <Box
      onClick={() => onOpen(r)}
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: done ? 'divider' : 'warning.light',
        bgcolor: done ? '#fff' : '#fffbeb',
        cursor: 'pointer',
        minWidth: 0,
        '&:hover': { boxShadow: 2 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {done
          ? <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
          : <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} color="warning" />}
        <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
          {r.employeeName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {dayjs(r.checkIn).format('HH:mm')}{r.checkOut ? `–${dayjs(r.checkOut).format('HH:mm')}` : ''}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" noWrap display="block" title={r.project?.name}>
        {r.project?.project_number ? `${r.project.project_number} · ` : ''}{r.project?.name || '-'}
      </Typography>
      {r.summary ? (
        <Typography
          variant="body2"
          sx={{
            mt: 0.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {r.summary}
        </Typography>
      ) : !r.attachments?.length && (
        <Typography variant="caption" color="warning.dark" sx={{ mt: 0.5, display: 'block' }}>
          ยังไม่ได้เขียนสรุป
        </Typography>
      )}
      {r.attachments?.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }} onClick={(e) => e.stopPropagation()}>
          {r.attachments.map((f) => (isImageUrl(f.url) ? (
            <a key={f.url} href={f.url} target="_blank" rel="noreferrer">
              <img
                src={f.url}
                alt={f.name}
                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', display: 'block' }}
              />
            </a>
          ) : (
            <Chip key={f.url} component="a" href={f.url} target="_blank" clickable size="small" label={f.name || 'ไฟล์แนบ'} sx={{ maxWidth: 150, height: 22, fontSize: '0.7rem' }} />
          )))}
        </Stack>
      )}
    </Box>
  );
}

export default function HrDailySummaries({ title }) {
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | pending | done
  const [selected, setSelected] = useState(null); // personKey ที่เลือกดูเฉพาะคน
  const [calOpen, setCalOpen] = useState(false);
  const [detail, setDetail] = useState(null); // แถวที่เปิดอ่านใน modal
  const [typeTab, setTypeTab] = useState(0); // 0 = รายวัน, 1 = รายเดือน (กรองเฉพาะการ์ดฝั่งขวา)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`${apiBase}/timestamp/daily-summaries`, { params: { date } });
      setRows(res?.rows || []);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    setSelected(null);
    load();
  }, [load]);

  const shiftDay = (n) => setDate(dayjs(date).add(n, 'day').format('YYYY-MM-DD'));

  // 1 แถวต่อคน (คนเดียวลงเวลาได้หลายรายการ) — "สรุปแล้ว" ถ้ามีอย่างน้อย 1 รายการที่เขียน
  const people = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = personKey(r);
      const prev = map.get(key);
      map.set(key, { key, name: r.employeeName, done: (prev?.done || false) || hasSummary(r) });
    });
    return [...map.values()].sort((a, b) => Number(a.done) - Number(b.done) || a.name.localeCompare(b.name, 'th'));
  }, [rows]);

  const doneCount = people.filter((p) => p.done).length;
  const pendingCount = people.length - doneCount;
  const percent = people.length ? Math.round((doneCount / people.length) * 100) : 0;
  const doneByKey = useMemo(() => new Map(people.map((p) => [p.key, p.done])), [people]);

  const matchFilter = (done) => (filter === 'all' ? true : filter === 'done' ? done : !done);
  const shownPeople = people.filter((p) => matchFilter(p.done));
  const shownRows = rows.filter((r) => (selected ? personKey(r) === selected : matchFilter(doneByKey.get(personKey(r)))));
  const rowsByTab = [shownRows.filter((r) => !isMonthly(r)), shownRows.filter(isMonthly)];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: { md: 'calc(100vh - 150px)' }, minHeight: 420 }}>
      {/* แถบหัว: ชื่อหน้า + ความคืบหน้า + เลือกวันที่ — บรรทัดเดียว */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={{ xs: 1, sm: 2 }} sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          {title || 'สรุปงานรายวัน'}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
            <Typography variant="caption" color="text.secondary">
              {loading ? 'กำลังโหลด...' : `สรุปแล้ว ${doneCount} / ${people.length} คน`}
            </Typography>
            <Typography variant="caption" fontWeight={700} color={pendingCount ? 'warning.dark' : 'success.main'}>
              {pendingCount ? `ค้าง ${pendingCount} คน` : people.length ? 'ครบแล้ว' : ''}
            </Typography>
          </Stack>
          <LinearProgress variant="determinate" value={percent} color={pendingCount ? 'warning' : 'success'} sx={{ height: 6, borderRadius: 3 }} />
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <IconButton size="small" onClick={() => shiftDay(-1)}><ChevronLeftIcon /></IconButton>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarMonthIcon />}
            onClick={() => setCalOpen(true)}
            sx={{ minWidth: 150, textTransform: 'none' }}
          >
            {dayjs(date).locale('th').format('D MMM YYYY')}
          </Button>
          <IconButton size="small" onClick={() => shiftDay(1)}><ChevronRightIcon /></IconButton>
        </Stack>
      </Stack>

      {/* เนื้อหา 2 ฝั่ง: ซ้าย = เช็กลิสต์รายชื่อ (กรอง/เลือกคน), ขวา = การ์ดสรุปงานเรียงเป็นตาราง */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '240px 1fr' },
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            maxHeight: { xs: 260, md: 'none' },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: '#fff',
          }}
        >
          <Stack direction="row" spacing={0.5} sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            {FILTERS.map(([key, label]) => (
              <Chip
                key={key}
                size="small"
                label={`${label} ${key === 'all' ? people.length : key === 'done' ? doneCount : pendingCount}`}
                color={filter === key ? 'primary' : 'default'}
                variant={filter === key ? 'filled' : 'outlined'}
                onClick={() => {
                  setFilter(key);
                  setSelected(null);
                }}
                sx={{ fontSize: '0.7rem', height: 24 }}
              />
            ))}
          </Stack>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 0.5 }}>
            {shownPeople.map((p) => (
              <Stack
                key={p.key}
                direction="row"
                alignItems="center"
                spacing={0.75}
                onClick={() => setSelected((cur) => (cur === p.key ? null : p.key))}
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: selected === p.key ? 'action.selected' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {p.done
                  ? <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
                  : <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} color="warning" />}
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>{p.name}</Typography>
              </Stack>
            ))}
            {!loading && shownPeople.length === 0 && (
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ py: 2 }}>
                ไม่มีรายชื่อ
              </Typography>
            )}
          </Box>
        </Box>

        {/* ฝั่งขวา: แท็บประเภทพนักงาน + การ์ด — สลับแท็บแล้วหน้าต่างเลื่อนซ้าย/ขวา (รายชื่อฝั่งซ้ายไม่เปลี่ยน) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          {/* แท็บแบบ segmented: แถบขาวเลื่อนตามแท็บที่เลือก */}
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              p: 0.5,
              mb: 1,
              borderRadius: 999,
              bgcolor: '#eef2f7',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                bottom: 4,
                left: 4,
                width: 'calc((100% - 8px) / 2)',
                borderRadius: 999,
                bgcolor: '#fff',
                boxShadow: '0 1px 4px rgba(15, 23, 42, 0.18)',
                transform: `translateX(${typeTab * 100}%)`,
                transition: 'transform 380ms cubic-bezier(0.22, 0.8, 0.3, 1)',
              }}
            />
            {[
              { label: 'พนักงานรายวัน', icon: <TodayIcon sx={{ fontSize: 18 }} />, count: rowsByTab[0].length },
              { label: 'พนักงานรายเดือน', icon: <EventNoteIcon sx={{ fontSize: 18 }} />, count: rowsByTab[1].length },
            ].map((t, i) => (
              <ButtonBase
                key={t.label}
                onClick={() => setTypeTab(i)}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  flex: 1,
                  py: 0.75,
                  gap: 0.75,
                  borderRadius: 999,
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: typeTab === i ? 'primary.main' : 'text.secondary',
                  transition: 'color 250ms ease',
                }}
              >
                {t.icon}
                {t.label}
                <Box
                  component="span"
                  sx={{
                    minWidth: 20,
                    px: 0.75,
                    borderRadius: 999,
                    fontSize: '0.7rem',
                    lineHeight: '20px',
                    bgcolor: typeTab === i ? 'primary.main' : 'rgba(100, 116, 139, 0.2)',
                    color: typeTab === i ? '#fff' : 'text.secondary',
                    transition: 'background-color 250ms ease, color 250ms ease',
                  }}
                >
                  {t.count}
                </Box>
              </ButtonBase>
            ))}
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                height: '100%',
                transform: `translateX(-${typeTab * 100}%)`,
                transition: 'transform 380ms cubic-bezier(0.22, 0.8, 0.3, 1)',
                willChange: 'transform',
              }}
            >
              {TYPE_TABS.map((tabKey, i) => (
                <Box
                  key={tabKey}
                  aria-hidden={typeTab !== i}
                  sx={{
                    flex: '0 0 100%',
                    minWidth: 0,
                    height: '100%',
                    overflowY: 'auto',
                    pr: 0.5,
                    // แท็บที่ไม่ได้เปิดอยู่จางลงขณะเลื่อน ให้ดูเป็นหน้าต่างบานเลื่อน
                    opacity: typeTab === i ? 1 : 0.25,
                    transition: 'opacity 380ms ease',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                      gap: 1,
                      alignContent: 'start',
                    }}
                  >
                    {rowsByTab[i].map((r) => <SummaryCard key={r.id} r={r} onOpen={setDetail} />)}
                  </Box>
                  {!loading && rowsByTab[i].length === 0 && (
                    <Typography color="text.secondary" textAlign="center" py={4}>
                      {rows.length === 0 ? 'ไม่มีรายการลงเวลาในวันนี้' : 'ไม่มีรายการในกลุ่มนี้'}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <SummaryDetailDialog r={detail} onClose={() => setDetail(null)} />

      {/* ปฏิทินเลือกวัน (เดือน/วันในสัปดาห์เป็นภาษาไทย) */}
      <Dialog open={calOpen} onClose={() => setCalOpen(false)}>
        <DialogContent sx={{ p: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <DateCalendar
              value={dayjs(date)}
              onChange={(v) => {
                if (v) setDate(v.format('YYYY-MM-DD'));
                setCalOpen(false);
              }}
              disableFuture
            />
          </LocalizationProvider>
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ pb: 1 }}>
            <Chip
              label="วันนี้"
              onClick={() => {
                setDate(dayjs().format('YYYY-MM-DD'));
                setCalOpen(false);
              }}
            />
            <Chip
              label="เมื่อวาน"
              onClick={() => {
                setDate(dayjs().subtract(1, 'day').format('YYYY-MM-DD'));
                setCalOpen(false);
              }}
            />
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
